import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FaCheck, FaTimes } from "react-icons/fa";

// Khung "Approval progress" ở cột phải trang chi tiết.
//
// Chuỗi ký có thể dài (luồng 1 là 6 bước, luồng 3 thêm Kế toán công nợ), nên danh
// sách bước có thanh cuộn RIÊNG: phần đầu (tiến độ + đang chờ ai) luôn đứng yên,
// chỉ danh sách cuộn. Mở trang ra là tự cuộn tới bước đang chờ ký.

const TONE = {
  done:     { dot: "tw-bg-geist-success tw-text-white tw-border-geist-success", bar: "tw-bg-geist-success", rail: "tw-bg-[#bfeae4]" },
  rejected: { dot: "tw-bg-geist-error tw-text-white tw-border-geist-error",     bar: "tw-bg-geist-error",   rail: "tw-bg-geist-border" },
  current:  { dot: "tw-bg-white tw-text-[#b46a00] tw-border-[#f0b45a]",         bar: "tw-bg-[#f0b45a]",     rail: "tw-bg-geist-border" },
  pending:  { dot: "tw-bg-white tw-text-geist-fg-3 tw-border-geist-border",      bar: "tw-bg-geist-border",  rail: "tw-bg-geist-border" },
  // Draft preview: a role nobody is configured for — Submit is refused until fixed.
  missing:  { dot: "tw-bg-[#fffaeb] tw-text-[#b54708] tw-border-[#fedf89]",      bar: "tw-bg-[#fedf89]",     rail: "tw-bg-geist-border" },
};

// Mọi bước chưa tới lượt đều mang is_pending, nên chỉ bước chưa ký ĐẦU TIÊN mới
// là bước đang chờ — không thì cả nửa dưới chuỗi cùng hiện "đang chờ ký".
const stateOf = (a, statusId, isFirstPending) => {
  if (a.is_approved) return "done";
  if (a.is_rejected) return "rejected";
  if (a.is_pending && statusId === 1 && isFirstPending) return "current";
  return "pending";
};

// Before Submit there are no approval rows: show the chain the request will get
// (server-built approval_preview), plus any role still missing a signer.
const previewSteps = (preview) => [
  ...(preview?.steps || []).map((st) => ({
    key: `preview-${st.seq}-${st.role_type}`,
    state: "pending",
    label: st.role_label,
    name: st.approver_name || st.approver_email,
    role: st.approver_position !== st.role_label ? st.approver_position : null,
  })),
  ...(preview?.missing || []).map((label) => ({
    key: `missing-${label}`, state: "missing", label, name: "Not configured — ask an administrator",
  })),
];

// "2026-10-22T09:15:00+07:00" -> "22/10/2026" (same as the signing dates above).
const shortDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

// Payment Accountant after BOD — not a signature: Processing → Paid.
const disbursementStep = (d) => {
  if (!d || !["expected", "processing", "paid"].includes(d.status)) return null;
  const team = (d.team || []).map((m) => m.name || m.email).join(", ");
  return {
    key: "disbursement",
    state: d.status === "paid" ? "done" : d.status === "processing" ? "current" : "pending",
    badge: d.status === "processing" ? "Processing" : null,
    label: d.status === "paid" ? "Paid" : d.status === "processing" ? "Processing" : "Payment",
    name: (d.status === "paid" && d.paid_by) || team || "Not configured — ask an administrator",
    role: "Payment Accountant",
    date: shortDate(d.paid_at),
    comment: d.status === "paid" ? [d.reference, d.note].filter(Boolean).join(" · ") : null,
  };
};

const buildSteps = (approvals, requestor, statusId, preview, disbursement) => {
  const firstPending = approvals.find((a) => a.is_pending && !a.is_approved && !a.is_rejected);
  const prepared = {
    key: "prepare",
    state: "done",
    label: "Prepared by",
    name: requestor?.name,
    role: requestor?.position,
    date: requestor?.date,
  };
  if (!approvals.length && preview) return [prepared, ...previewSteps(preview)];
  const payment = disbursementStep(disbursement);
  return [
  prepared,
  ...approvals.map((a) => ({
    key: a.id,
    state: stateOf(a, statusId, a === firstPending),
    label: a.block_label,
    name: a.approver_name || a.approver_email,
    role: a.approver_position,
    date: a.signed_at,
    comment: a.comment,
  })),
  ...(payment ? [payment] : []),
  ];
};

const stepShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  state: PropTypes.oneOf(Object.keys(TONE)).isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  role: PropTypes.string,
  date: PropTypes.string,
  comment: PropTypes.string,
  badge: PropTypes.string,
});

const Summary = ({ steps, statusId }) => {
  const current = steps.find((s) => s.state === "current");
  const rejected = steps.find((s) => s.state === "rejected");

  if (rejected) {
    return <p className="tw-m-0 tw-text-[12px] tw-text-geist-error">Rejected at <b>{rejected.label}</b></p>;
  }
  if (statusId === 2) {
    return (
      <p className="tw-m-0 tw-text-[12px] tw-text-geist-success">
        Fully approved
        {current?.key === "disbursement" && <span className="tw-text-[#b46a00]"> · payment processing</span>}
      </p>
    );
  }
  if (statusId === 4) return <p className="tw-m-0 tw-text-[12px] tw-text-geist-fg-3">Canceled</p>;
  if (current) {
    return (
      <p className="tw-m-0 tw-text-[12px] tw-text-geist-fg-2 tw-truncate">
        Waiting on <b className="tw-text-geist-fg">{current.name || "—"}</b>
        <span className="tw-text-geist-fg-3"> · {current.label}</span>
      </p>
    );
  }
  const planned = steps.length - 1;
  return (
    <p className="tw-m-0 tw-text-[12px] tw-text-geist-fg-3">
      Not submitted yet{planned > 0 ? ` · ${planned} approvals after Submit` : ""}
    </p>
  );
};

Summary.propTypes = {
  steps: PropTypes.arrayOf(stepShape).isRequired,
  statusId: PropTypes.number,
};


const Step = React.forwardRef(({ step, n, isLast, nextDone, action }, ref) => {
  const tone = TONE[step.state];
  const isCurrent = step.state === "current";
  return (
    <li ref={ref} className="tw-relative tw-flex tw-gap-3 tw-list-none">
      {/* Rail nối sang bước kế — xanh khi cả hai đầu đã ký */}
      {!isLast && (
        <span
          className={`tw-absolute tw-left-[11px] tw-top-6 tw-bottom-0 tw-w-[2px] tw-rounded-full ${
            step.state === "done" && nextDone ? TONE.done.rail : "tw-bg-geist-border"
          }`}
        />
      )}

      <span
        className={`tw-relative tw-z-10 tw-mt-2 tw-w-6 tw-h-6 tw-shrink-0 tw-rounded-full tw-border-2 tw-border-solid tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-semibold ${tone.dot}`}
      >
        {step.state === "done" ? <FaCheck size={9} />
          : step.state === "rejected" ? <FaTimes size={9} />
          : step.state === "missing" ? "!"
          : isCurrent ? <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#f0b45a] tw-animate-pulse" />
          : n}
      </span>

      <div
        className={`tw-flex-1 tw-min-w-0 tw-mb-2 tw-px-3 tw-py-2 tw-rounded-geist-sm ${
          isCurrent ? "tw-bg-[#fffaf2] tw-ring-1 tw-ring-[#f7e2c0]" : ""
        }`}
      >
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
          <span className={`tw-text-[13px] tw-font-semibold tw-truncate ${step.state === "pending" ? "tw-text-geist-fg-3" : "tw-text-geist-fg"}`}>
            {step.label}
          </span>
          {step.date && !["pending", "missing"].includes(step.state) && (
            <span className="tw-shrink-0 tw-text-[11px] tw-tabular-nums tw-text-geist-fg-3">{step.date}</span>
          )}
          {isCurrent && (
            <span className="tw-shrink-0 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-[#b46a00]">
              {step.badge || "Awaiting"}
            </span>
          )}
        </div>
        <p className={`tw-m-0 tw-mt-0.5 tw-text-[12px] tw-truncate ${step.state === "missing" ? "tw-text-[#b54708]" : "tw-text-geist-fg-2"}`}>
          {step.name || "—"}
          {step.role && <span className="tw-text-geist-fg-3"> · {step.role}</span>}
        </p>
        {step.comment && (
          <p className="tw-m-0 tw-mt-1.5 tw-pl-2 tw-border-0 tw-border-l-2 tw-border-solid tw-border-geist-border tw-text-[12px] tw-italic tw-text-geist-fg-2 tw-break-words">
            {step.comment}
          </p>
        )}
        {action}
      </div>
    </li>
  );
});

Step.displayName = "Step";

Step.propTypes = {
  step: stepShape.isRequired,
  n: PropTypes.number.isRequired,
  isLast: PropTypes.bool,
  nextDone: PropTypes.bool,
  action: PropTypes.node,
};

// `disbursementAction`: what the Payment Accountant can do now (Mark as paid / send to
// HSBC) — shown on the Processing step only.
const ApprovalProgress = ({ approvals = [], preview = null, requestor, statusId, disbursement = null, disbursementAction = null, className = "" }) => {
  const steps = buildSteps(approvals, requestor, statusId, preview, disbursement);
  const done = steps.filter((s) => s.state === "done").length;

  const scrollRef = useRef(null);
  const focusRef = useRef(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    setFade({
      top: el.scrollTop > 4,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 4,
    });
  };

  // Cuộn tới bước đang chờ (hoặc bước bị từ chối) — chỉ trong khung, không kéo cả trang.
  const focusKey = (steps.find((s) => s.state === "current" || s.state === "rejected") || {}).key;
  useEffect(() => {
    const el = scrollRef.current;
    const row = focusRef.current;
    if (el && row) el.scrollTop = Math.max(0, row.offsetTop - el.clientHeight / 3);
    updateFade();
  }, [focusKey, steps.length]);

  return (
    <section className={`tw-flex tw-flex-col tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden ${className}`}>
      <header className="tw-px-4 tw-pt-3.5 tw-pb-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-shrink-0">
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-geist-fg">Approval progress</h3>
          <span className="tw-text-[12px] tw-tabular-nums tw-text-geist-fg-3">
            <b className="tw-text-geist-fg">{done}</b>/{steps.length}
          </span>
        </div>
        <div className="tw-flex tw-gap-1 tw-mt-2.5" aria-hidden>
          {steps.map((s) => (
            <span key={s.key} className={`tw-h-1 tw-flex-1 tw-rounded-full ${TONE[s.state].bar}`} />
          ))}
        </div>
        <div className="tw-mt-2">
          <Summary steps={steps} statusId={statusId} />
        </div>
      </header>

      <div className="tw-relative tw-min-h-0 tw-flex-1">
        <ol
          ref={scrollRef}
          onScroll={updateFade}
          className="inbox-scroll tw-m-0 tw-px-4 tw-pt-3 tw-pb-1 tw-overflow-y-auto tw-max-h-[50vh] lg:tw-max-h-[calc(100dvh-15rem)]"
        >
          {steps.map((step, i) => (
            <Step
              key={step.key}
              ref={step.key === focusKey ? focusRef : undefined}
              step={step}
              n={i + 1}
              isLast={i === steps.length - 1}
              nextDone={steps[i + 1]?.state === "done"}
              action={step.key === "disbursement" && step.state === "current" ? disbursementAction : null}
            />
          ))}
        </ol>
        {/* Gợi ý còn bước phía trên/dưới khi danh sách dài hơn khung */}
        {fade.top && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-6 tw-bg-gradient-to-b tw-from-geist-bg tw-to-transparent" />}
        {fade.bottom && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-8 tw-bg-gradient-to-t tw-from-geist-bg tw-to-transparent" />}
      </div>
    </section>
  );
};

const disbursementShape = PropTypes.shape({
  status: PropTypes.string,
  team: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, email: PropTypes.string })),
  paid_by: PropTypes.string,
  paid_at: PropTypes.string,
  reference: PropTypes.string,
  note: PropTypes.string,
});

ApprovalProgress.propTypes = {
  approvals: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_approved: PropTypes.bool,
    is_rejected: PropTypes.bool,
    is_pending: PropTypes.bool,
    block_label: PropTypes.string,
    approver_name: PropTypes.string,
    approver_email: PropTypes.string,
    approver_position: PropTypes.string,
    signed_at: PropTypes.string,
    comment: PropTypes.string,
  })),
  preview: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.shape({
      seq: PropTypes.number,
      role_type: PropTypes.string,
      role_label: PropTypes.string,
      approver_name: PropTypes.string,
      approver_email: PropTypes.string,
      approver_position: PropTypes.string,
    })),
    missing: PropTypes.arrayOf(PropTypes.string),
  }),
  requestor: PropTypes.shape({
    name: PropTypes.string,
    position: PropTypes.string,
    date: PropTypes.string,
  }),
  statusId: PropTypes.number,
  disbursement: disbursementShape,
  disbursementAction: PropTypes.node,
  className: PropTypes.string,
};


export default ApprovalProgress;
